import React, { useEffect } from 'react';
import {
  VStack,
  Button,
  Text,
  Grid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  CheckboxGroup,
  Checkbox,
  Textarea,
  Box,
  Link,
} from '@chakra-ui/react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { contactSchema } from '@/features/OurProducts/schema'
import { useContact } from '@/features/OurProducts/hooks/useContact'
import { SERVICE_OPTIONS } from '@/features/OurProducts/constants'

import { Link as NextLink } from '@/lib/Link'
import { useUTMContext } from '@/lib/gtm/utmContext'

const inputStyle = {
  backgroundColor: 'white',
  borderRadius: '0',
  h: 12,
  color: 'JAGUAR_BLACK',
}

const defaultValues = {
  name: '',
  surname: '',
  email: '',
  tel: '',
  additionalDetail: '',
  acceptedTerm: false,
  agreedToMarketingConsent: false,
  interestedProducts: [],
  url: '',
}

function ContactForm(): React.ReactElement {
  const {
    register,
    watch,
    handleSubmit,
    reset: resetForm,
    control,
    formState: { isSubmitting, errors },
    setValue,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultValues,
    resolver: zodResolver(contactSchema),
  })

  const { isLoading, mutate } = useContact()

  const { data: utm } = useUTMContext()

  useEffect(() => {
    setValue('url', window.location.href);
  }, [setValue]);

  return (
    <Box
      as="section"
      sx={{
        w: 'full',
        px: {
          base: 8,
          md: '100px',
          'lg+': 4,
        },
        py: {
          base: 20,
          'lg+': 20,
        },
        backgroundColor: '#0d1d2e',
        backgroundImage:
          'linear-gradient(to top, rgba(13, 29, 46, 0), #121a1e), linear-gradient(to bottom, #0d1d2e, #0d1d2e)',
        color: 'white',
      }}
    >
      <form
        onSubmit={handleSubmit((data) =>
          mutate({ ...data, utm }, { onSuccess: () => resetForm() }),
        )}
      >
        <VStack
          spacing={{ base: 7, sm: 12, 'lg+': 16 }}
          sx={{ alignItems: 'stretch', maxW: '951px', mx: 'auto' }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Text as="h1" sx={{ fontSize: '4xl', fontWeight: 'medium' }}>
              ติดต่อเพื่อรับบริการอื่นเพิ่มเติม
            </Text>
            <Text
              sx={{
                fontSize: { base: 'lg', 'lg+': '2xl' },
                fontWeight: 'light',
              }}
            >
              รับรายละเอียดเพิ่มเติมหรือประเมินราคาให้เหมาะสมกับธุรกิจของคุณ
            </Text>
          </Box>
          <Grid
            sx={{
              gridTemplateColumns: {
                base: '1fr',
                'lg+': 'repeat(2, 1fr)',
              },
              gridGap: 6,
            }}
          >
            <FormControl isRequired isInvalid={Boolean(errors.name)}>
              <FormLabel>ชื่อจริง</FormLabel>
              <Input
                placeholder="ชื่อจริง"
                sx={inputStyle}
                {...register('name')}
              />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={Boolean(errors.surname)}>
              <FormLabel>นามสกุล</FormLabel>
              <Input
                placeholder="นามสกุล"
                sx={inputStyle}
                {...register('surname')}
              />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={Boolean(errors.email)}>
              <FormLabel>อีเมล</FormLabel>
              <Input
                placeholder="อีเมล"
                inputMode="email"
                sx={inputStyle}
                {...register('email')}
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={Boolean(errors.tel)}>
              <FormLabel>เบอร์โทรศัพท์</FormLabel>
              <Input
                placeholder="เบอร์โทรศัพท์"
                type="tel"
                sx={inputStyle}
                {...register('tel')}
              />
              <FormErrorMessage>{errors.tel?.message}</FormErrorMessage>
            </FormControl>
            <Controller
              name="interestedProducts"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <FormControl
                    isInvalid={Boolean(errors.additionalDetail)}
                    sx={{ gridColumn: { 'lg+': '1 / 3' } }}
                  >
                    <FormLabel>บริการที่สนใจ (เลือกได้มากกว่า 1)</FormLabel>
                    <CheckboxGroup onChange={onChange} value={value}>
                      <Grid
                        sx={{
                          gridTemplateColumns: {
                            base: '1fr',
                            'lg+': 'repeat(2, 1fr)',
                          },
                          gridGap: '4px 24px',
                        }}
                      >
                        {SERVICE_OPTIONS.map((option, index) => {
                          return (
                            <Checkbox
                              key={`${index}`}
                              value={option}
                              size="lg"
                              sx={{
                                alignItems: 'flex-start',
                                span: { fontSize: 'md' },
                              }}
                            >
                              {option}
                            </Checkbox>
                          )
                        })}
                      </Grid>
                    </CheckboxGroup>
                    <FormErrorMessage>
                      {errors.additionalDetail?.message}
                    </FormErrorMessage>
                  </FormControl>
                )
              }}
            />
            <FormControl
              isInvalid={Boolean(errors.additionalDetail)}
              sx={{ gridColumn: { 'lg+': '1 / 3' } }}
            >
              <FormLabel>ข้อมูลสอบถามเพิ่มเติม</FormLabel>
              <Textarea
                placeholder="รายละเอียดที่อยากสอบถาม"
                sx={{ ...inputStyle, height: 28 }}
                {...register('additionalDetail')}
              />
              <FormErrorMessage>
                {errors.additionalDetail?.message}
              </FormErrorMessage>
            </FormControl>
            <VStack
              spacing="4"
              sx={{
                alignItems: 'flex-start',
                fontWeight: 'medium',
                gridColumn: { 'lg+': '1 / 3' },
              }}
            >
              <Checkbox
                isRequired
                size="lg"
                colorScheme="primary"
                sx={{ alignItems: 'flex-start' }}
                {...register('acceptedTerm')}
                isChecked={watch('acceptedTerm')}
              >
                ฉันยอมรับ{' '}
                <NextLink route="terms" passHref>
                  <Link isExternal>เงื่อนไขในการใช้เว็บไซต์</Link>
                </NextLink>
                &nbsp; และ{' '}
                <NextLink route="privacy" passHref>
                  <Link isExternal>นโยบายความเป็นส่วนตัว</Link>
                </NextLink>
              </Checkbox>
              <VStack sx={{ alignItems: 'stretch', fontWeight: 'light' }}>
                <Checkbox
                  size="lg"
                  colorScheme="primary"
                  sx={{ alignItems: 'flex-start' }}
                  {...register('agreedToMarketingConsent')}
                  isChecked={watch('agreedToMarketingConsent')}
                >
                  ฉันต้องการรับโปรโมชันและข่าวสารทางการตลาดเกี่ยวกับ PDPA Pro
                  และบริการอื่น ๆ จากเรา บริษัทในเครือ
                  บริษัทย่อยและพันธมิตรทางธุรกิจ &#8203;
                  <span style={{ whiteSpace: 'nowrap' }}>
                    (คุณสามารถยกเลิกได้ทุกเมื่อ)
                  </span>
                </Checkbox>
              </VStack>
            </VStack>
          </Grid>
          <Button
            isLoading={isLoading || isSubmitting}
            type="submit"
            size="xl"
            colorScheme="primary"
            sx={{
              maxW: '450px',
              w: 'full',
              fontSize: 'xl',
              h: '72px',
              alignSelf: { base: 'stretch', sm: 'center' },
            }}
          >
            ติดต่อเรา
          </Button>
        </VStack>
      </form>
    </Box>
  )
}

export default ContactForm
